import React, { useEffect, useState } from 'react';
import { Grid, Typography } from '@material-ui/core';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import KpiCard from 'components/charts/KpiCard';
import TimeSeriesChart from 'components/charts/TimeSeriesChart';
import { startRequest, finishRequest } from 'store/modules/loading/actions';
import { setMessage } from 'store/modules/message/action';
import { sanitizeErrorUI } from 'utils/errorUtil';
import { getTraceabilityProductionDailyReport } from 'services/traceabilityProductionDailyReport/service';
import DatePickers from 'components/v2/datePicker';

const DashboardWrapper = styled.div`
  min-height: 100vh;
  background: #f5f5f5;
  padding: 24px;
`;

const DashboardCard = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  background: #ffffff;
  border-radius: 12px;
  padding: 24px 24px 32px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
`;

const Section = styled.div`
  margin-top: 24px;
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
`;

const KpiSectionTitle = styled.h3`
  margin: 0 0 12px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #9e9e9e;
`;

function utcToBrazilTime(timestamp) {
  if (!timestamp) return timestamp;

  // transforma em ISO UTC
  const utcDate = new Date(
    timestamp.replace(' ', 'T') + ':00Z'
  );

  return utcDate.toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const ProductionDashboard = () => {
  const dispatch = useDispatch();

  // ============================
  // DATA INICIAL = HOJE
  // ============================

  const [date, setDate] = useState(new Date());

  const [summary, setSummary] = useState(null);

  const [loading, setLoading] = useState(false);

  // ============================
  // FORMATADOR YYYY-MM-DD
  // ============================

  function formatDate(parDateObj) {
    var dateObj = new Date(parDateObj);
    if (!dateObj) return '';

    const year = dateObj.getFullYear();

    const month = String(
      dateObj.getMonth() + 1
    ).padStart(2, '0');

    const day = String(
      dateObj.getDate()
    ).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  // ============================
  // FETCH
  // ============================

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        dispatch(startRequest());
        var formattedDate = formatDate(date);

        const response =
          await getTraceabilityProductionDailyReport(
            formattedDate
          );

        const data = response.data;
        data.general = data.general.map((item) => ({
          ...item,
          timestamp: utcToBrazilTime(item.timestamp),
        }));
        Object.keys(data.lines).forEach((line) => {
          data.lines[line] = data.lines[line].map((item) => ({
            ...item,
            timestamp: utcToBrazilTime(item.timestamp),
          }));
        });

        setSummary(data);

      } catch (err) {
        console.log(err);
        dispatch(setMessage(sanitizeErrorUI(err)));
      } finally {
        setLoading(false);

        dispatch(finishRequest());
      }
    }

    fetchData();

  }, [date, dispatch]);

  const isLoadingOrEmpty =
    loading || !summary;

  return (
    <DashboardWrapper>
      <DashboardCard>

        {/* ========================= */}
        {/* HEADER */}
        {/* ========================= */}

        <Grid
          container
          spacing={3}
          alignItems="center"
        >
          <Grid item xs={12} md={6}>
            <Typography variant="h5">
              Dashboard de Produção
            </Typography>
          </Grid>

          <DatePickers
            xs={3}
            name="productionDate"
            label="Data"
            value={date}
            onChange={(selectedDate) =>
              setDate(selectedDate)
            }
            cancelLabel="Cancelar"
            okLabel="Salvar"
            hasWarning={false}
          />
        </Grid>

        {/* ========================= */}
        {/* LOADING */}
        {/* ========================= */}

        {isLoadingOrEmpty ? (
          <Section>
            <Typography>
              {loading
                ? 'Carregando dados...'
                : 'Sem dados para a data selecionada.'}
            </Typography>
          </Section>
        ) : (
          <>
            {/* ========================= */}
            {/* KPIs */}
            {/* ========================= */}

            <Section>
              <KpiSectionTitle>
                Indicadores Gerais
              </KpiSectionTitle>

              <KpiGrid>

                <KpiCard
                  label="Total unidades"
                  value={summary.kpis.totalUnits}
                  unit="un"
                />

                <KpiCard
                  label="Total kg"
                  value={summary.kpis.totalKg}
                  unit="kg"
                />

                <KpiCard
                  label="Média kg/h"
                  value={summary.kpis.avgKgPerHour}
                  unit="kg/h"
                />

                <KpiCard
                  label="Média un/h"
                  value={summary.kpis.avgUnitsPerHour}
                  unit="un/h"
                />

                <KpiCard
                  label="Melhor linha"
                  value={summary.kpis.bestLine.line}
                />

                <KpiCard
                  label="Kg melhor linha"
                  value={summary.kpis.bestLine.kg}
                  unit="kg"
                />

                <KpiCard
                  label="Pico kg"
                  value={summary.kpis.peakKgInterval.kg}
                  unit="kg"
                />

                <KpiCard
                  label="Pico unidades"
                  value={summary.kpis.peakUnitsInterval.units}
                  unit="un"
                />

              </KpiGrid>
            </Section>

            {/* ========================= */}
            {/* GRÁFICO GERAL */}
            {/* ========================= */}

            <Section>

              <Grid container spacing={3}>

                <Grid item xs={12}>

                  <TimeSeriesChart
                    title="Produção geral"
                    data={summary.general}
                    xKey="timestamp"
                    series={[
                      /* {
                        key: 'kg',
                        label: 'Kg',
                        stroke: '#388e3c',
                      }, */
                      {
                        key: 'units',
                        label: 'Unidades',
                        stroke: '#1976d2',
                      },
                    ]}
                  />

                </Grid>

              </Grid>

            </Section>

            {/* ========================= */}
            {/* GRÁFICOS POR LINHA */}
            {/* ========================= */}

            <Section>

              <KpiSectionTitle>
                Produção por linha
              </KpiSectionTitle>

              <Grid container spacing={3}>

                {Object.entries(summary.lines).map(
                  ([line, data]) => (
                    <Grid
                      item
                      xs={12}
                      md={6}
                      key={line}
                    >
                      <TimeSeriesChart
                        title={`Linha ${line}`}
                        data={data}
                        xKey="timestamp"
                        series={[
                          /* {
                            key: 'kg',
                            label: 'Kg',
                            stroke: '#388e3c',
                          }, */
                          {
                            key: 'units',
                            label: 'Unidades',
                            stroke: '#1976d2',
                          },
                        ]}
                      />
                    </Grid>
                  )
                )}

              </Grid>

            </Section>
          </>
        )}
      </DashboardCard>
    </DashboardWrapper>
  );
};

export default ProductionDashboard;